import ByteChunksView from '../byteChunksView';
import { byteChunksToInt } from '../utils';
import PngChunkView from './pngChunkView';

/**
 * the type of frame area disposal to be done after rendering a frame
 */
export enum DisposeOperation
    {
    /**
   * no disposal is done on this frame before rendering the next;
   * the contents of the output buffer are left as is.
   */
    NONE = 0,
    /**
   * the frame's region of the output buffer is to be cleared to fully
   * transparent black before rendering the next frame.
   */
    BACKGROUND = 1,
    /**
   * the frame's region of the output buffer is to be reverted to the previous
   * contents before rendering the next frame.
   */
    PREVIOUS = 2,
}

export enum BlendOperation
    {
    /**
   * all color components of the frame, including alpha, overwrite the current
   * contents of the frame's output buffer region.
   */
    SOURCE = 0,
    /**
   * the frame should be composited onto the output buffer based on its alpha
   */
    OVER = 1,
}

export default class FrameControlChunkView extends PngChunkView
{
    private readonly rawSequenceNumber: ByteChunksView;
    private readonly rawWidth: ByteChunksView;
    private readonly rawHeight: ByteChunksView;
    private readonly rawX: ByteChunksView;
    private readonly rawY: ByteChunksView;
    private readonly rawDelayNumerator: ByteChunksView;
    private readonly rawDelayDenominator: ByteChunksView;
    private readonly rawDispose: ByteChunksView;
    private readonly rawBlend: ByteChunksView;

    constructor(rawData: Uint8Array[])
    {
        super(rawData, 26);

        let tail = this.rawContent;

        [this.rawSequenceNumber, tail] = tail.split(4);
        [this.rawWidth, tail] = tail.split(4);
        [this.rawHeight, tail] = tail.split(4);
        [this.rawX, tail] = tail.split(4);
        [this.rawY, tail] = tail.split(4);
        [this.rawDelayNumerator, tail] = tail.split(2);
        [this.rawDelayDenominator, tail] = tail.split(2);
        [this.rawDispose, this.rawBlend] = tail.split(1);

        if (!(this.dispose in DisposeOperation))
        {
            throw new Error(`Unknown dispose operation ${this.dispose}`);
        }

        if (!(this.blend in BlendOperation))
        {
            throw new Error(`Unknown blend operation ${this.blend}`);
        }
    }

    /**
   * sequence number of the animation chunk, starting from 0.
   */
    get sequenceNumber(): number
    {
        return byteChunksToInt(this.rawSequenceNumber.getRawChunks());
    }

    /**
   * width of the following frame
   */
    get width(): number
    {
        return byteChunksToInt(this.rawWidth.getRawChunks());
    }

    /**
   * height of the following frame
   */
    get height(): number
    {
        return byteChunksToInt(this.rawHeight.getRawChunks());
    }

    /**
   * x position of the following frame
   */
    get x(): number
    {
        return byteChunksToInt(this.rawX.getRawChunks());
    }

    /**
   * y position of the following frame
   */
    get y(): number
    {
        return byteChunksToInt(this.rawY.getRawChunks());
    }

    /**
   * the numerator of the delay fraction; indicating the time to display the
   * current frame, in seconds.
   */
    get delayNumerator(): number
    {
        return byteChunksToInt(this.rawDelayNumerator.getRawChunks());
    }

    /**
   * the denominator of the delay fraction; indicating the time to display the
   * current frame, in seconds.
   */
    get delayDenominator(): number
    {
        return byteChunksToInt(this.rawDelayDenominator.getRawChunks()) || 100;
    }

    /**
   * the type of frame area disposal to be done after rendering this frame.
   */
    get dispose(): DisposeOperation
    {
        return byteChunksToInt(this.rawDispose.getRawChunks());
    }

    /**
   * whether the frame is to be alpha blended into the current output buffer content,
   * or whether it should completely replace its region in the output buffer.
   */
    get blend(): BlendOperation
    {
        return byteChunksToInt(this.rawBlend.getRawChunks());
    }
}
